import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Plus, Search, Trash2, X, AlertCircle, Edit2, Users, CheckCircle, Clock
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserCoach } from '../../../lib/auth';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  getClients, createClient, updateClient, deleteClient,
  getSessions, createSession, updateSession, deleteSession,
  type CoachingClient, type CoachingSession,
} from '../../../lib/coaching-sessions';

type View = 'clients' | 'sessions' | 'calendar';
type ModalMode = 'add-client' | 'edit-client' | 'add-session' | 'edit-session' | null;

export function CoachingSessionsTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [clients, setClients] = useState<CoachingClient[]>([]);
  const [sessions, setSessions] = useState<CoachingSession[]>([]);
  const [selectedClient, setSelectedClient] = useState<CoachingClient | null>(null);
  const [view, setView] = useState<View>('clients');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [clientForm, setClientForm] = useState({
    full_name: '', email: '', phone: '', goals: '', notes: '', status: 'active',
  });
  const [sessionForm, setSessionForm] = useState({
    session_date: new Date().toISOString().slice(0, 10),
    duration_minutes: '60',
    session_type: 'video',
    notes: '',
    follow_ups: '',
    client_id: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) { navigate('/tr/login'); return; }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) { navigate('/tr/login'); return; }
        setCoachId(coach.id);

        const [c, s] = await Promise.all([getClients(coach.id), getSessions(coach.id)]);
        setClients(c);
        setSessions(s);
      } catch (err) {
        console.error(err);
        setError('Kocluk verileri yuklenemedi');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const reload = async () => {
    if (!coachId) return;
    const [c, s] = await Promise.all([getClients(coachId), getSessions(coachId)]);
    setClients(c);
    setSessions(s);
  };

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId) return;
    setIsSubmitting(true);
    try {
      const { error: err } = await createClient({
        coach_id: coachId,
        full_name: clientForm.full_name,
        email: clientForm.email,
        phone: clientForm.phone || undefined,
        goals: clientForm.goals || undefined,
        notes: clientForm.notes || undefined,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetClientForm();
    } catch (err: any) {
      setError(err.message || 'Danisan eklenemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) return;
    setIsSubmitting(true);
    try {
      const { error: err } = await updateClient(selectedClient.id, {
        full_name: clientForm.full_name,
        email: clientForm.email,
        phone: clientForm.phone || null,
        goals: clientForm.goals || null,
        notes: clientForm.notes || null,
        status: clientForm.status,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetClientForm();
    } catch (err: any) {
      setError(err.message || 'Danisan guncellenemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = async (id: string) => {
    if (!confirm('Bu danisani ve tum seanslarini silmek istediginizden emin misiniz?')) return;
    await deleteClient(id);
    if (selectedClient?.id === id) setSelectedClient(null);
    await reload();
  };

  const handleAddSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coachId) return;
    setIsSubmitting(true);
    try {
      const { error: err } = await createSession({
        client_id: sessionForm.client_id || selectedClient?.id || '',
        coach_id: coachId,
        session_date: sessionForm.session_date,
        duration_minutes: parseInt(sessionForm.duration_minutes),
        session_type: sessionForm.session_type,
        notes: sessionForm.notes || undefined,
        follow_ups: sessionForm.follow_ups || undefined,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetSessionForm();
    } catch (err: any) {
      setError(err.message || 'Seans eklenemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSession = async (e: React.FormEvent, sessionId: string) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error: err } = await updateSession(sessionId, {
        session_date: sessionForm.session_date,
        duration_minutes: parseInt(sessionForm.duration_minutes),
        session_type: sessionForm.session_type,
        notes: sessionForm.notes || null,
        follow_ups: sessionForm.follow_ups || null,
      });
      if (err) throw err;
      await reload();
      setModalMode(null);
      resetSessionForm();
    } catch (err: any) {
      setError(err.message || 'Seans guncellenemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSession = async (id: string) => {
    if (!confirm('Bu seansi silmek istediginizden emin misiniz?')) return;
    await deleteSession(id);
    await reload();
  };

  const openEditClient = (client: CoachingClient) => {
    setSelectedClient(client);
    setClientForm({
      full_name: client.full_name,
      email: client.email,
      phone: client.phone || '',
      goals: client.goals || '',
      notes: client.notes || '',
      status: client.status,
    });
    setModalMode('edit-client');
  };

  const openAddSession = (client?: CoachingClient) => {
    resetSessionForm();
    if (client) {
      setSessionForm(f => ({ ...f, client_id: client.id }));
    }
    setModalMode('add-session');
  };

  const openEditSession = (session: CoachingSession) => {
    setSessionForm({
      session_date: session.session_date,
      duration_minutes: String(session.duration_minutes),
      session_type: session.session_type,
      notes: session.notes || '',
      follow_ups: session.follow_ups || '',
      client_id: session.client_id,
    });
    setModalMode('edit-session');
    setSelectedClient(null);
    (window as any).__editingSessionId = session.id;
  };

  const resetClientForm = () => {
    setClientForm({ full_name: '', email: '', phone: '', goals: '', notes: '', status: 'active' });
  };
  const resetSessionForm = () => {
    setSessionForm({
      session_date: new Date().toISOString().slice(0, 10),
      duration_minutes: '60', session_type: 'video', notes: '', follow_ups: '', client_id: '',
    });
  };

  const filteredClients = clients.filter(c =>
    c.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const clientSessions = selectedClient
    ? sessions.filter(s => s.client_id === selectedClient.id)
    : sessions;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-sage-100 text-sage-700';
      case 'paused': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-cream-200 text-chocolate-600';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'paused': return 'Duraklatildi';
      case 'completed': return 'Tamamlandi';
      default: return status;
    }
  };

  const getSessionTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return 'Video';
      case 'phone': return 'Telefon';
      case 'in_person': return 'Yuz Yuze';
      default: return type;
    }
  };

  const getViewLabel = (v: View) => {
    switch (v) {
      case 'clients': return 'Danisanlar';
      case 'sessions': return 'Seanslar';
      case 'calendar': return 'Takvim';
    }
  };

  // Calendar: group sessions by month
  const calendarSessions = [...sessions].sort((a, b) => b.session_date.localeCompare(a.session_date));
  const sessionsByMonth: Record<string, CoachingSession[]> = {};
  calendarSessions.forEach(s => {
    const month = s.session_date.slice(0, 7);
    if (!sessionsByMonth[month]) sessionsByMonth[month] = [];
    sessionsByMonth[month].push(s);
  });

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Kocluk seanslari yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-teal-500 rounded-xl shadow-soft">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">Kocluk Seanslari</h1>
                <p className="freud-text-sm text-chocolate-500">
                  {clients.length} danisan / {sessions.length} seans kaydedildi
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { resetClientForm(); setModalMode('add-client'); }}
                className="freud-btn-secondary flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Danisan
              </button>
              <button
                onClick={() => openAddSession()}
                className="freud-btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Seans Ekle
              </button>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            {(['clients', 'sessions', 'calendar'] as const).map(v => (
              <button
                key={v}
                onClick={() => { setView(v); setSelectedClient(null); }}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                  view === v ? 'bg-teal-600 text-white' : 'bg-cream-200 text-chocolate-600 hover:bg-cream-300'
                }`}
              >
                {getViewLabel(v)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {error && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-6">
        {view === 'clients' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="freud-card-elevated">
                <div className="p-4 border-b border-cream-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chocolate-400" />
                    <input
                      type="text"
                      placeholder="Danisan ara..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="freud-input pl-9 text-sm"
                    />
                  </div>
                </div>
                <div className="divide-y divide-cream-200 max-h-[600px] overflow-y-auto">
                  {filteredClients.map(client => (
                    <button
                      key={client.id}
                      onClick={() => setSelectedClient(client)}
                      className={`w-full p-4 text-left hover:bg-cream-50 transition-colors ${
                        selectedClient?.id === client.id ? 'bg-cream-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-chocolate-800">{client.full_name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(client.status)}`}>
                          {getStatusLabel(client.status)}
                        </span>
                      </div>
                      <p className="text-xs text-chocolate-500 mt-0.5">{client.email}</p>
                      <p className="text-xs text-chocolate-400 mt-1">
                        {sessions.filter(s => s.client_id === client.id).length} seans
                      </p>
                    </button>
                  ))}
                  {filteredClients.length === 0 && (
                    <div className="p-8 text-center text-chocolate-400 text-sm">
                      Henuz danisan yok. Yukaridaki butonu kullanarak ilk danisaninizi ekleyin.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              {selectedClient ? (
                <div className="space-y-4">
                  <div className="freud-card-elevated p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-chocolate-900">{selectedClient.full_name}</h2>
                        <p className="text-sm text-chocolate-500">{selectedClient.email}</p>
                        {selectedClient.phone && <p className="text-sm text-chocolate-400">{selectedClient.phone}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditClient(selectedClient)} className="freud-btn-secondary text-sm px-3 py-1.5">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => openAddSession(selectedClient)} className="freud-btn-primary text-sm px-3 py-1.5 flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5" /> Seans
                        </button>
                      </div>
                    </div>
                    {selectedClient.goals && (
                      <div className="mt-4 p-3 bg-sage-50 rounded-lg">
                        <p className="text-xs font-semibold text-sage-700 mb-1">Hedefler</p>
                        <p className="text-sm text-chocolate-700">{selectedClient.goals}</p>
                      </div>
                    )}
                    {selectedClient.notes && (
                      <div className="mt-3 p-3 bg-cream-100 rounded-lg">
                        <p className="text-xs font-semibold text-chocolate-500 mb-1">Notlar</p>
                        <p className="text-sm text-chocolate-700">{selectedClient.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="freud-card-elevated">
                    <div className="p-4 border-b border-cream-200">
                      <h3 className="text-sm font-semibold text-chocolate-800">Seans Gecmisi</h3>
                    </div>
                    <div className="divide-y divide-cream-200">
                      {clientSessions.length === 0 ? (
                        <div className="p-8 text-center text-chocolate-400 text-sm">
                          Henuz seans kaydedilmedi.
                        </div>
                      ) : clientSessions.map(session => (
                        <div key={session.id} className="p-4 hover:bg-cream-50 group">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-teal-50 rounded-lg">
                                <Clock className="w-4 h-4 text-teal-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-chocolate-800">
                                  {new Date(session.session_date).toLocaleDateString('tr-TR', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                                <p className="text-xs text-chocolate-500">
                                  {session.duration_minutes} dakika - {getSessionTypeIcon(session.session_type)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openEditSession(session)} className="p-1.5 text-chocolate-400 hover:text-chocolate-600 rounded">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDeleteSession(session.id)} className="p-1.5 text-chocolate-400 hover:text-coral-600 rounded">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          {session.notes && <p className="text-sm text-chocolate-600 mt-2 ml-11">{session.notes}</p>}
                          {session.follow_ups && (
                            <div className="mt-2 ml-11 p-2 bg-amber-50 rounded-lg">
                              <p className="text-xs font-medium text-amber-700">Takip Adimlari:</p>
                              <p className="text-sm text-amber-800">{session.follow_ups}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="freud-card-elevated p-12 text-center">
                  <Users className="w-12 h-12 text-chocolate-300 mx-auto mb-3" />
                  <p className="text-chocolate-500">Detaylarini ve seanslarini goruntulemek icin bir danisan secin</p>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'sessions' && (
          <div className="freud-card-elevated">
            <div className="p-4 border-b border-cream-200">
              <h3 className="text-sm font-semibold text-chocolate-800">Tum Seanslar</h3>
            </div>
            <div className="divide-y divide-cream-200">
              {sessions.length === 0 ? (
                <div className="p-12 text-center text-chocolate-400">Henuz seans yok.</div>
              ) : sessions.map(session => {
                const client = clients.find(c => c.id === session.client_id);
                return (
                  <div key={session.id} className="p-4 hover:bg-cream-50 group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-teal-50 rounded-lg">
                          <Clock className="w-4 h-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-chocolate-800">
                            {client?.full_name || 'Bilinmeyen Danisan'}
                          </p>
                          <p className="text-xs text-chocolate-500">
                            {new Date(session.session_date).toLocaleDateString('tr-TR')} - {session.duration_minutes} dakika - {getSessionTypeIcon(session.session_type)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEditSession(session)} className="p-1.5 text-chocolate-400 hover:text-chocolate-600 rounded">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDeleteSession(session.id)} className="p-1.5 text-chocolate-400 hover:text-coral-600 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {session.notes && <p className="text-sm text-chocolate-600 mt-2 ml-11">{session.notes}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === 'calendar' && (
          <div className="space-y-6">
            {Object.keys(sessionsByMonth).length === 0 ? (
              <div className="freud-card-elevated p-12 text-center text-chocolate-400">
                Henuz seans yok. Yukaridaki butonu kullanarak ilk seansinizi kaydedin.
              </div>
            ) : Object.entries(sessionsByMonth).map(([month, monthSessions]) => (
              <div key={month} className="freud-card-elevated">
                <div className="p-4 border-b border-cream-200">
                  <h3 className="text-sm font-semibold text-chocolate-800">
                    {new Date(month + '-01').toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                  </h3>
                  <p className="text-xs text-chocolate-400">{monthSessions.length} seans</p>
                </div>
                <div className="divide-y divide-cream-200">
                  {monthSessions.map(session => {
                    const client = clients.find(c => c.id === session.client_id);
                    return (
                      <div key={session.id} className="p-4 flex items-center gap-4">
                        <div className="w-12 text-center">
                          <p className="text-lg font-bold text-chocolate-800">
                            {new Date(session.session_date).getDate()}
                          </p>
                          <p className="text-xs text-chocolate-400">
                            {new Date(session.session_date).toLocaleDateString('tr-TR', { weekday: 'short' })}
                          </p>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-chocolate-800">{client?.full_name || 'Bilinmeyen'}</p>
                          <p className="text-xs text-chocolate-500">{session.duration_minutes} dakika - {getSessionTypeIcon(session.session_type)}</p>
                        </div>
                        {session.follow_ups && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Takip</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Danisan Modali */}
      <AnimatePresence>
        {(modalMode === 'add-client' || modalMode === 'edit-client') && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="freud-card-elevated p-6 w-full max-w-md m-4 max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-xl font-bold text-chocolate-900 mb-6">
                {modalMode === 'edit-client' ? 'Danisani Duzenle' : 'Danisan Ekle'}
              </h2>
              <form onSubmit={modalMode === 'edit-client' ? handleEditClient : handleAddClient} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Ad Soyad</label>
                  <input type="text" value={clientForm.full_name} onChange={(e) => setClientForm({ ...clientForm, full_name: e.target.value })} required className="freud-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">E-posta</label>
                  <input type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} required className="freud-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Telefon</label>
                  <input type="text" value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} className="freud-input" />
                </div>
                {modalMode === 'edit-client' && (
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">Durum</label>
                    <select value={clientForm.status} onChange={(e) => setClientForm({ ...clientForm, status: e.target.value })} className="freud-input">
                      <option value="active">Aktif</option>
                      <option value="paused">Duraklatildi</option>
                      <option value="completed">Tamamlandi</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Hedefler</label>
                  <textarea value={clientForm.goals} onChange={(e) => setClientForm({ ...clientForm, goals: e.target.value })} rows={3} className="freud-input resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Notlar</label>
                  <textarea value={clientForm.notes} onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })} rows={3} className="freud-input resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setModalMode(null); resetClientForm(); }} className="freud-btn-secondary flex-1" disabled={isSubmitting}>Iptal</button>
                  <button type="submit" className="freud-btn-primary flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Kaydediliyor...' : (modalMode === 'edit-client' ? 'Kaydet' : 'Danisan Ekle')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Seans Modali */}
      <AnimatePresence>
        {(modalMode === 'add-session' || modalMode === 'edit-session') && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="freud-card-elevated p-6 w-full max-w-md m-4 max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-xl font-bold text-chocolate-900 mb-6">
                {modalMode === 'edit-session' ? 'Seansi Duzenle' : 'Seans Ekle'}
              </h2>
              <form
                onSubmit={(e) => {
                  if (modalMode === 'edit-session') {
                    handleEditSession(e, (window as any).__editingSessionId);
                  } else {
                    handleAddSession(e);
                  }
                }}
                className="space-y-4"
              >
                {modalMode === 'add-session' && (
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">Danisan</label>
                    <select
                      value={sessionForm.client_id}
                      onChange={(e) => setSessionForm({ ...sessionForm, client_id: e.target.value })}
                      required
                      className="freud-input"
                    >
                      <option value="">Danisan secin</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.full_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Tarih</label>
                  <input type="date" value={sessionForm.session_date} onChange={(e) => setSessionForm({ ...sessionForm, session_date: e.target.value })} required className="freud-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">Sure (dakika)</label>
                    <input type="number" value={sessionForm.duration_minutes} onChange={(e) => setSessionForm({ ...sessionForm, duration_minutes: e.target.value })} min="15" step="15" required className="freud-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-chocolate-700 mb-1">Tur</label>
                    <select value={sessionForm.session_type} onChange={(e) => setSessionForm({ ...sessionForm, session_type: e.target.value })} className="freud-input">
                      <option value="video">Video</option>
                      <option value="phone">Telefon</option>
                      <option value="in_person">Yuz Yuze</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Seans Notlari</label>
                  <textarea value={sessionForm.notes} onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })} rows={4} className="freud-input resize-none" placeholder="Bu seansta neler ele alindi..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-chocolate-700 mb-1">Takip Adimlari / Sonraki Adimlar</label>
                  <textarea value={sessionForm.follow_ups} onChange={(e) => setSessionForm({ ...sessionForm, follow_ups: e.target.value })} rows={3} className="freud-input resize-none" placeholder="Bir sonraki seans icin yapilacaklar..." />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setModalMode(null); resetSessionForm(); }} className="freud-btn-secondary flex-1" disabled={isSubmitting}>Iptal</button>
                  <button type="submit" className="freud-btn-primary flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Kaydediliyor...' : (modalMode === 'edit-session' ? 'Kaydet' : 'Seans Ekle')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
